import cn from 'classnames';
import React, { FC, Suspense, memo, useMemo } from 'react';

import Typography from '@mui/material/Typography';
import { VariantTypography } from '@src/types/common/common.types';

import { getTokenListForUi } from '@utils/tokens.utils';

import roundWarning from '@assets/icons/roundWarning.svg?url';

import TokensIcon from '@components/common/TokensIcon';
import Tooltip from '@components/common/Tooltip';
import FarmTags from '@components/vaults/FarmDetails/FarmTags';
import StakeTokenLinks from '@components/vaults/StakeTokenLinks';

import { CHAIN_ID, CHAIN_INFO } from '@constants/common/chain.constants';
import { TokenSize } from '@constants/common/ui.constants';

import { CommonTokenDto } from '@dto/common/tokens.dto';

import useStyles from './styles.module';

interface Props {
  chainId: number;
  stakingToken: CommonTokenDto;
  isPoolV3PositionOutRange?: boolean;
  percentage?: {
    farm: string;
    stakedPercent: string;
  };
  enabledFarmData?: {
    dexFactoryName: string;
    origin: string;
  };
  farmTagsData?: {
    dateOfAdd?: string;
    poolV3Fee?: number | undefined;
  };
  className?: string;
  classNameTags?: string;
  size?: TokenSize;
  withChainLabel?: boolean;
  symbolVariant?: VariantTypography;
}

const FarmDetails: FC<Props> = ({
  classNameTags,
  className,
  isPoolV3PositionOutRange,
  percentage = undefined,
  stakingToken,
  chainId,
  enabledFarmData,
  farmTagsData,
  size = TokenSize.Large,
  withChainLabel = false,
  symbolVariant = 'subtitle2',
}) => {
  const { classes } = useStyles();

  const ChainLabel = useMemo(
    () =>
      React.lazy(() =>
        Promise.resolve({
          default: () => (
            <img src={CHAIN_INFO[chainId as CHAIN_ID].logo} alt="" />
          ),
        }),
      ),
    [chainId],
  );

  const Tokens = useMemo(
    () =>
      React.lazy(() =>
        Promise.resolve({
          default: () => (
            <TokensIcon
              tokens={getTokenListForUi(stakingToken)}
              size={size || TokenSize.Large}
            />
          ),
        }),
      ),
    [size, stakingToken],
  );

  return (
    <div className={cn(classes.box, className)}>
      <div className={classes.tokensBox}>
        <Suspense fallback={<div className={classes.tokenCircle} />}>
          <Tokens />
        </Suspense>
        {withChainLabel && (
          <Suspense fallback={<div className={classes.chainCircle} />}>
            <div className={classes.chainLabel}>
              <ChainLabel />
            </div>
          </Suspense>
        )}
      </div>
      <div className={classes.symbol}>
        <Typography
          variant={symbolVariant || 'subtitle2'}
          color={(theme) =>
            isPoolV3PositionOutRange ? theme.accent.a4 : theme.primary.p2
          }
        >
          {stakingToken?.symbol}
        </Typography>
        {enabledFarmData ? (
          <StakeTokenLinks
            chainId={chainId}
            dexFactoryName={enabledFarmData.dexFactoryName}
            stakeToken={stakingToken}
            isPoolV3PositionOutRange={isPoolV3PositionOutRange}
          />
        ) : (
          <Tooltip title="This farm has been retired">
            <img src={roundWarning} alt="" />
          </Tooltip>
        )}
      </div>
      {percentage && (
        <>
          <Typography variant="subtitle2" color={(theme) => theme.primary.p3}>
            {percentage.farm}%
          </Typography>
          {/* {toBN(percentage.stakedPercent).gt(0) && ( */}
          {/*  <Typography variant="subtitle2" color={(theme) => theme.accent.a5}> */}
          {/*    ({percentage.stakedPercent}%) */}
          {/*  </Typography> */}
          {/* )} */}
        </>
      )}
      {farmTagsData && (
        <FarmTags
          dateOfAdd={farmTagsData.dateOfAdd}
          poolFee={farmTagsData.poolV3Fee}
          stakingTokenType={stakingToken.type}
          className={classNameTags}
        />
      )}
    </div>
  );
};

export default memo(FarmDetails);
